import React, { useState, useEffect } from 'react';
import { X, CheckCircle } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

const OnboardingOverlay = ({ user, onClose, profileComplete }) => {
  const navigate = useNavigate();
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    // Check if user has seen onboarding
    const hasSeenOnboarding = localStorage.getItem(`onboarding_seen_${user.id}`);
    
    // Don't show overlay if:
    // 1. User has already seen it
    // 2. Profile is complete (for providers)
    if (!hasSeenOnboarding && !profileComplete) {
      setVisible(true);
    }
  }, [user.id, profileComplete]);

  const handleClose = () => {
    localStorage.setItem(`onboarding_seen_${user.id}`, 'true');
    setVisible(false);
    if (onClose) onClose();
  };

  const handleStart = () => {
    handleClose();
    if (user.role === 'provider') {
      navigate('/provider/onboarding');
    } else {
      navigate('/event-konfigurator');
    }
  };

  if (!visible) return null;

  const providerSteps = [
    'Profil ausfüllen (Firma, Standort, Kategorien)',
    'Portfolio-Bilder hochladen',
    'Kategorien und Unterkategorien festlegen',
    'Abo aktivieren, um für Kunden sichtbar zu werden'
  ];

  const customerSteps = [
    'Event-Art wählen (Hochzeit, Geburtstag, etc.)',
    'Dienstleister in deiner Region finden',
    'Profile vergleichen und Anfragen senden',
    'Direkt mit Dienstleistern kommunizieren'
  ];

  const steps = user.role === 'provider' ? providerSteps : customerSteps;
  const title = user.role === 'provider' ? 'Willkommen bei EventConnect!' : 'Willkommen bei EventConnect!';
  const subtitle = user.role === 'provider' 
    ? 'So startest du als Dienstleister:'
    : 'So nutzt du die Plattform als Kunde:';

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm" data-testid="onboarding-overlay">
      <div className="bg-white rounded-3xl shadow-2xl p-8 max-w-2xl mx-4 relative">
        <button
          onClick={handleClose}
          className="absolute top-4 right-4 p-2 hover:bg-gray-100 rounded-full transition-colors"
          data-testid="onboarding-close"
        >
          <X className="w-6 h-6 text-gray-600" />
        </button>

        <div className="text-center mb-8">
          <div className="w-20 h-20 bg-gradient-to-br from-[#2B2142] to-[#3D3158] rounded-full flex items-center justify-center mx-auto mb-6">
            <span className="text-4xl">👋</span>
          </div>
          <h2 className="text-3xl font-bold text-gray-900 mb-2">{title}</h2>
          <p className="text-lg text-gray-600">{subtitle}</p>
        </div>

        <div className="space-y-4 mb-8">
          {steps.map((step, idx) => (
            <div key={idx} className="flex items-start gap-4 p-4 bg-gradient-to-br from-[#FFF0D1]/30 to-[#F4F2F2] rounded-xl">
              <div className="flex-shrink-0 w-8 h-8 bg-[#2B2142] rounded-full flex items-center justify-center text-white font-bold">
                {idx + 1}
              </div>
              <p className="text-gray-700 font-medium">{step}</p>
            </div>
          ))}
        </div>

        <div className="flex gap-4">
          <button
            onClick={handleClose}
            className="flex-1 py-3 border-2 border-gray-300 hover:border-gray-400 text-gray-700 font-semibold rounded-xl transition-colors"
            data-testid="onboarding-skip"
          >
            Später
          </button>
          <button
            onClick={handleStart}
            className="flex-1 py-3 bg-[#2B2142] hover:bg-[#1E1730] text-white font-semibold rounded-xl transition-colors"
            data-testid="onboarding-start"
          >
            {user.role === 'provider' ? 'Profil erstellen' : 'Event planen'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default OnboardingOverlay;
